import { createFileRoute, Link } from "@tanstack/react-router";
import { sanitizeHtml } from "@/lib/sanitize-html";
import { supabase } from "@/integrations/supabase/client";
import { ShareMenu } from "@/components/ShareMenu";
import { BlogTOC, type BlogHeading } from "@/components/BlogTOC";
import { ReadingProgress } from "@/components/ReadingProgress";
import { PostNav, type NavPost } from "@/components/PostNav";
import { lazy, Suspense, useEffect, useState } from "react";
const AudioReader = lazy(() =>
  import("@/components/AudioReader").then((m) => ({ default: m.AudioReader })),
);
import { Download, Link as LinkIcon, Check, Mail, ArrowLeft } from "lucide-react";
import { bakeWatermark } from "@/lib/bake-watermark";
import { slugifyAssetName } from "@/lib/image-compress";
import scottAvatar from "@/assets/scott-avatar.webp";
import { readingTimeMinutes, wordCount } from "@/lib/reading-time";
import { NewsletterForm } from "@/components/NewsletterForm";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedPosts, type RelatedPost } from "@/components/RelatedPosts";
import { coverSrcSet } from "@/lib/cover-image";
import { transformSupabaseImage } from "@/lib/supabase-image";
import { formatTag, slugifyTag } from "@/lib/tags";

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  cover_image_url: string | null;
  cover_alt: string | null;
  tags: string[];
  seo_title: string | null;
  seo_description: string | null;
  published_at: string | null;
  updated_at?: string | null;
  category_id: string | null;
  category: { name: string; slug: string } | null;
  secondary_category: { name: string; slug: string } | null;
};

type Backlink = { id: string; slug: string; title: string };

function slugifyHeading(value: string) {
  return value
    .toLowerCase()
    .replace(/&[a-z0-9#]+;/gi, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

function stripHtml(value: string) {
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function addHeadingIds(html: string): { html: string; headings: BlogHeading[] } {
  const used = new Set<string>();
  const headings: BlogHeading[] = [];

  const nextId = (base: string) => {
    let id = base || `section-${headings.length + 1}`;
    let unique = id;
    let index = 2;
    while (used.has(unique)) unique = `${id}-${index++}`;
    used.add(unique);
    return unique;
  };

  const updated = html.replace(
    /<h([1-3])\b([^>]*)>([\s\S]*?)<\/h\1>/gi,
    (match, level, attrs, inner) => {
      const text = stripHtml(inner);
      const existingId = attrs.match(/\sid=(['"])(.*?)\1/i)?.[2];
      const id = nextId(existingId || slugifyHeading(text));
      const cleanedAttrs = attrs.replace(/\s+id=(['"]).*?\1/i, "");
      const numericLevel = Number(level);
      // SEO: ensure only one H1 per page — downgrade content h1s to h2
      if (numericLevel === 1) {
        headings.push({ id, text, level: 2 });
        return `<h2${cleanedAttrs} id="${id}" class="h1-downgraded">${inner}</h2>`;
      }
      headings.push({ id, text, level: numericLevel });
      return `<h${level}${cleanedAttrs} id="${id}">${inner}</h${level}>`;
    },
  );

  return { html: updated, headings };
}

export const Route = createFileRoute("/blog/$slug")({
  component: PostPage,
  loader: async ({ params }) => {
    const { data } = await supabase
      .from("posts")
      .select(
        "*, category:categories!posts_category_id_fkey(name, slug), secondary_category:categories!posts_secondary_category_id_fkey(name, slug)",
      )
      .eq("slug", params.slug)
      .eq("status", "published")
      .maybeSingle();
    const post = data as Post | null;

    // Related (same primary category, not current) + prev/next by published_at
    let prev: NavPost = null;
    let next: NavPost = null;
    let backlinks: Backlink[] = [];
    let related: RelatedPost[] = [];
    if (post) {
      if (post.published_at) {
        const [{ data: prevRow }, { data: nextRow }] = await Promise.all([
          supabase
            .from("posts")
            .select(
              "slug, title, excerpt, cover_image_url, published_at, category:categories!posts_category_id_fkey(name, slug)",
            )
            .eq("status", "published")
            .lt("published_at", post.published_at)
            .order("published_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from("posts")
            .select(
              "slug, title, excerpt, cover_image_url, published_at, category:categories!posts_category_id_fkey(name, slug)",
            )
            .eq("status", "published")
            .gt("published_at", post.published_at)
            .order("published_at", { ascending: true })
            .limit(1)
            .maybeSingle(),
        ]);
        prev = (prevRow as NavPost) ?? null;
        next = (nextRow as NavPost) ?? null;
      }
      const { data: bl } = await supabase
        .from("posts")
        .select("id, slug, title")
        .eq("status", "published")
        .neq("id", post.id)
        .ilike("content", `%/blog/${post.slug}%`)
        .order("published_at", { ascending: false })
        .limit(6);
      backlinks = (bl as Backlink[]) ?? [];

      // Related: prefer posts that share tags or category (excluding current).
      // Fetch a small candidate set, score by overlap, take top 3.
      const { data: candidates } = await supabase
        .from("posts")
        .select(
          "id, slug, title, excerpt, cover_image_url, published_at, tags, category_id, secondary_category_id",
        )
        .eq("status", "published")
        .neq("id", post.id)
        .order("published_at", { ascending: false })
        .limit(40);
      type Candidate = RelatedPost & {
        tags: string[] | null;
        category_id: string | null;
        secondary_category_id: string | null;
      };
      const tagSet = new Set((post.tags ?? []).map((t) => t.toLowerCase()));
      const scored = ((candidates as Candidate[]) ?? [])
        .map((c) => {
          const sharedTags = (c.tags ?? []).reduce(
            (acc, t) => acc + (tagSet.has(t.toLowerCase()) ? 1 : 0),
            0,
          );
          const sameCat =
            post.category_id &&
            (c.category_id === post.category_id || c.secondary_category_id === post.category_id)
              ? 1
              : 0;
          return { post: c, score: sharedTags * 2 + sameCat };
        })
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 3)
        .map((x) => ({
          id: x.post.id,
          slug: x.post.slug,
          title: x.post.title,
          excerpt: x.post.excerpt,
          cover_image_url: x.post.cover_image_url,
          published_at: x.post.published_at,
        }));
      related = scored;
    }
    return { post, prev, next, backlinks, related };
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    const url = `https://scottcbusiness.com/blog/${params.slug}`;
    if (!post) {
      return {
        meta: [
      { name: "twitter:card", content: "summary_large_image" },

          { title: "Post not found | Scott Cunningham" },
          { name: "description", content: "This Field Notes article could not be found. Browse Scott Cunningham’s latest writing." },
          { property: "og:title", content: "Post not found | Scott Cunningham" },
          { property: "og:description", content: "This Field Notes article could not be found. Browse Scott Cunningham’s latest writing." },
          { name: "robots", content: "noindex" },
          { property: "og:type", content: "article" },
          { property: "og:url", content: url },
        ],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = post.seo_title || post.title;
    const description = post.seo_description || post.excerpt || `Read ${post.title} in Field Notes by Scott Cunningham (@scottcbusiness).`;
    const image = `https://scottcbusiness.com/api/og/${params.slug}.png`;
    const imageAlt = `${post.title}, Field Notes by Scott Cunningham`;
    const articleWordCount = wordCount(post.content || "");
    const keywords = (post.tags ?? []).join(", ") || undefined;
    const sectionName = post.category?.name || undefined;
    const categorySlug = post.category?.slug;
    const breadcrumbItems = [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://scottcbusiness.com/" },
      {
        "@type": "ListItem",
        position: 2,
        name: "Field Notes",
        item: "https://scottcbusiness.com/blog",
      },
      ...(sectionName && categorySlug
        ? [
            {
              "@type": "ListItem",
              position: 3,
              name: sectionName,
              item: `https://scottcbusiness.com/blog?category=${categorySlug}`,
            },
          ]
        : []),
      {
        "@type": "ListItem",
        position: sectionName && categorySlug ? 4 : 3,
        name: post.title,
        item: url,
      },
    ];
    return {
      meta: [
        { title },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: image },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: imageAlt },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
        { name: "twitter:image:alt", content: imageAlt },
        ...(post.published_at
          ? [{ property: "article:published_time", content: post.published_at }]
          : []),
        ...(post.updated_at
          ? [{ property: "article:modified_time", content: post.updated_at }]
          : []),
        ...(sectionName ? [{ property: "article:section", content: sectionName }] : []),
        ...(post.tags ?? []).map((t) => ({ property: "article:tag", content: t })),
      ],
      links: [
        { rel: "canonical", href: url },
        ...(post.cover_image_url
          ? [
              {
                rel: "preload",
                as: "image",
                href: transformSupabaseImage(post.cover_image_url, {
                  width: 1600,
                  format: "webp",
                  quality: 80,
                }),
                fetchPriority: "high" as const,
              },
            ]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            inLanguage: "en",
            headline: post.title,
            description,
            image,
            wordCount: articleWordCount,
            ...(keywords ? { keywords } : {}),
            ...(sectionName ? { articleSection: sectionName } : {}),
            datePublished: post.published_at ?? undefined,
            dateModified: post.updated_at ?? post.published_at ?? undefined,
            author: {
              "@type": "Person",
              "@id": "https://scottcbusiness.com/#person",
              name: "Scott Cunningham",
              url: "https://scottcbusiness.com/about",
              image: "https://scottcbusiness.com/og-image.jpg",
              sameAs: [
                "https://www.linkedin.com/in/scottcbusiness/",
                "https://x.com/scottcbusiness",
                "https://www.youtube.com/@scottcbusiness",
                "https://bsky.app/profile/scottcbusiness.com",
                "https://www.facebook.com/ScottCBusiness/",
              ],
            },
            publisher: {
              "@type": "Organization",
              "@id": "https://scottcbusiness.com/#organization",
              name: "Scott Cunningham",
              url: "https://scottcbusiness.com",
              logo: {
                "@type": "ImageObject",
                url: "https://scottcbusiness.com/og-default.jpg",
                width: 1200,
                height: 630,
              },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbItems,
          }),
        },
      ],
    };
  },
});

function PostPage() {
  const { post, prev, next, backlinks, related } = Route.useLoaderData();
  if (!post)
    return (
      <div className="relative overflow-hidden">
        <div
          className="blob float-slow -left-32 -top-24 h-[320px] w-[320px] bg-[var(--color-primary)]"
          style={{ opacity: 0.18 }}
        />
        <div
          className="blob float-slow right-[-120px] top-32 h-[300px] w-[300px] bg-[var(--color-electric)]"
          style={{ animationDelay: "-3s", opacity: 0.18 }}
        />
        <div
          className="blob float-slow bottom-[-160px] left-1/3 h-[340px] w-[340px] bg-[var(--color-violet)]"
          style={{ animationDelay: "-6s", opacity: 0.18 }}
        />
        <div className="relative mx-auto flex min-h-[70vh] max-w-2xl flex-col items-start justify-center px-6 py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-foreground/15 bg-background/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/80 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-primary)]" />
            404 · Field Notes
          </span>
          <h1 className="mt-5 text-[clamp(2.25rem,6vw,4rem)] leading-[0.95] tracking-tight text-foreground">
            This post doesn’t exist
          </h1>
          <div
            aria-hidden="true"
            className="mt-4 h-1 w-24 rounded-full"
            style={{
              backgroundImage:
                "linear-gradient(90deg, oklch(0.66 0.26 28), oklch(0.6 0.28 305), oklch(0.6 0.26 265))",
            }}
          />
          <p className="mt-6 max-w-md text-base text-foreground/70">
            The link you followed may be broken, or the post may have been moved or unpublished.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/blog"
              className="inline-flex items-center rounded-full bg-foreground px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-background transition-transform hover:-translate-y-0.5"
            >
              Browse Field Notes
            </Link>
            <Link
              to="/"
              className="inline-flex items-center rounded-full border border-foreground/20 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/80 transition-colors hover:border-foreground hover:text-foreground"
            >
              Go home
            </Link>
          </div>
        </div>
      </div>
    );

  const readMinutes = readingTimeMinutes(post.content);
  const dateLabel = post.published_at
    ? new Date(post.published_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;
  const metaParts = [
    post.category?.name ? { text: post.category.name } : null,
    dateLabel ? { text: dateLabel } : null,
  ].filter(Boolean) as { text: string; hideOnMobile?: boolean }[];

  // Wrap every <img> inside the post body with a figure that carries a watermark + © caption.
  // The cover image is rendered separately above and not affected.
  // Also force lazy/async loading on every body image so long posts don't
  // pre-fetch off-screen images and we don't block the main thread.
  // Downgrade any content <h1> to <h2> so the page only has one H1 (the post title).
  const demotedContent = post.content
    .replace(/<h1(\b[^>]*)>/gi, "<h2$1>")
    .replace(/<\/h1>/gi, "</h2>");
  // Strip trailing `;` (or other stray punctuation) from href attributes that
  // sometimes leak in from pasted content and break the crawled URL.
  const cleanedLinks = demotedContent.replace(
    /href\s*=\s*"([^"]+)"/gi,
    (_m: string, url: string) => `href="${url.replace(/[;,]+$/, "")}"`,
  );
  // Strip rel="nofollow" from every link. Editorial outbound citations should
  // pass authority; internal links must never be nofollowed.
  const noSelfNofollow = cleanedLinks.replace(
    /<a\b([^>]*?)>/gi,
    (match: string, attrs: string) => {
      const cleaned = attrs.replace(/\srel\s*=\s*"([^"]*)"/i, (_r, rel: string) => {
        const filtered = rel
          .split(/\s+/)
          .filter((t) => t.toLowerCase() !== "nofollow")
          .join(" ")
          .trim();
        return filtered ? ` rel="${filtered}"` : "";
      });
      return `<a${cleaned}>`;
    },
  );
  const wrappedContent = noSelfNofollow.replace(
    /<img\b([^>]*)\/?>(?!\s*<\/figure>)/gi,
    (_match: string, rawAttrs: string) => {
      let attrs = rawAttrs;
      if (!/\bloading\s*=/.test(attrs)) attrs += ' loading="lazy"';
      if (!/\bdecoding\s*=/.test(attrs)) attrs += ' decoding="async"';
      if (!/\bfetchpriority\s*=/i.test(attrs)) attrs += ' fetchpriority="low"';
      // Ensure every body <img> has an alt attribute for accessibility + SEO.
      if (!/\balt\s*=/i.test(attrs)) {
        const safeTitle = post.title.replace(/"/g, "&quot;");
        attrs += ` alt="Image accompanying ${safeTitle}"`;
      }
      return `<figure class="blog-img"><img${attrs} /><figcaption class="blog-img__caption">© Scott Cunningham</figcaption></figure>`;
    },
  );
  // Sanitize the post HTML to prevent stored XSS. Only allow http(s)/mailto/tel
  // URLs on attributes — strips javascript:, data:, etc.
  const sanitizedContent = sanitizeHtml(wrappedContent, {
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
  });
  const { html: renderedContent, headings } = addHeadingIds(sanitizedContent);

  // After the post HTML is in the DOM, bake the SCS watermark into every inline
  // body image so right-click → Save delivers the watermarked file (not the raw original).
  useEffect(() => {
    const imgs = document.querySelectorAll<HTMLImageElement>("figure.blog-img > img");
    const created: string[] = [];
    const postSlug = slugifyAssetName(post.title);
    imgs.forEach((img) => {
      if (img.dataset.watermarked === "1") return;
      img.dataset.watermarked = "1";
      const original = img.currentSrc || img.src;
      // Derive a nice filename from the alt text (falls back to post title).
      const altSlug = img.alt ? slugifyAssetName(img.alt, "") : "";
      const niceName =
        (altSlug ? `${postSlug}-${altSlug}` : postSlug).replace(/-+/g, "-") + ".webp";
      bakeWatermark(original)
        .then((url) => {
          created.push(url);
          img.src = url;
          img.removeAttribute("srcset");
          // Wrap the img in an anchor so "Save link as" / drag-out uses the proper name.
          // (Browsers cannot override the filename for "Save image as" on a blob: URL.)
          if (!img.parentElement?.matches("a.blog-img__dl")) {
            const a = document.createElement("a");
            a.href = url;
            a.download = niceName;
            a.className = "blog-img__dl";
            a.setAttribute("aria-label", `Download ${niceName}`);
            img.replaceWith(a);
            a.appendChild(img);
          } else {
            (img.parentElement as HTMLAnchorElement).href = url;
            (img.parentElement as HTMLAnchorElement).download = niceName;
          }
        })
        .catch((err) => {
          // CORS or load failure — fall back to original (no watermark) silently
          console.warn("[blog] watermark bake failed", err);
        });
    });
    return () => {
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [renderedContent, post.title]);

  // Smooth-scroll to deep-linked heading + inject hover-anchor links + clipboard copy
  useEffect(() => {
    const article = document.querySelector("article");
    if (!article) return;

    // Inject anchor links into headings
    const heads = article.querySelectorAll("h1[id], h2[id], h3[id]");
    heads.forEach((h) => {
      if (h.querySelector("a.heading-anchor")) return;
      const a = document.createElement("a");
      a.className = "heading-anchor no-print";
      a.href = `#${h.id}`;
      a.setAttribute("aria-label", "Copy link to this section");
      a.textContent = "#";
      h.appendChild(a);
    });

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const a = target.closest("a.heading-anchor") as HTMLAnchorElement | null;
      if (!a) return;
      e.preventDefault();
      const id = a.getAttribute("href")?.slice(1);
      if (!id) return;
      const el = document.getElementById(id);
      if (!el) return;
      const fullUrl = `${window.location.origin}${window.location.pathname}#${id}`;
      navigator.clipboard?.writeText(fullUrl).catch(() => {});
      history.replaceState(null, "", `#${id}`);
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("heading-anchor-flash");
      setTimeout(() => el.classList.remove("heading-anchor-flash"), 1400);
    };
    article.addEventListener("click", onClick);

    // Smooth scroll to existing hash on load
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    return () => article.removeEventListener("click", onClick);
  }, [renderedContent]);

  return (
    <div className="blog-scope bg-foreground text-background">
      <ReadingProgress />
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-4 py-16 sm:px-8 md:px-12 lg:py-24 min-[1151px]:grid-cols-[18rem_minmax(0,48rem)_1fr] min-[1151px]:px-6">
        <BlogTOC headings={headings} totalReadMinutes={readMinutes} title={post.title} />
        <article className="min-w-0 mx-auto w-full max-w-3xl min-[1151px]:mx-0 min-[1151px]:max-w-none">
          {/* Breadcrumb kept in JSON-LD only (see head()); visible row hidden for cleaner design. */}
          <Link
            to="/blog"
            className="no-print mb-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 transition-colors hover:text-white"
            aria-label="Back to Field Notes"
          >
            <ArrowLeft className="h-3 w-3" />
            Back
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            <Suspense fallback={null}>
              <AudioReader />
            </Suspense>
            <CopyLinkButton url={`https://scottcbusiness.com/blog/${post.slug}`} />
            <button
              type="button"
              onClick={() => window.print()}
              aria-label="Download this post as PDF"
              className="no-print inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 backdrop-blur transition-colors hover:bg-white/10 hover:text-white"
            >
              <Download className="h-3 w-3" />
              <span className="hidden sm:inline">Download as PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
            <ShareMenu url={`https://scottcbusiness.com/blog/${post.slug}`} title={post.title} />
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 normal-case tracking-normal transition-colors hover:text-white"
            >
              <img
                src={scottAvatar}
                alt=""
                width={28}
                height={28}
                loading="lazy"
                decoding="async"
                className="h-7 w-7 rounded-full border border-white/20 object-cover"
              />
              <span className="text-[11px] font-medium tracking-[0.18em] uppercase">
                Scott Cunningham
              </span>
            </Link>
            {metaParts.map((item, i) => (
              <span
                key={i}
                className={`inline-flex items-center leading-none ${item.hideOnMobile ? "hidden sm:inline-flex" : ""}`}
              >
                <span className="mr-3">·</span>
                {item.text}
              </span>
            ))}
          </div>
          <h1 className="mt-3 font-display text-4xl tracking-tight text-white [overflow-wrap:anywhere] lg:text-6xl">
            {post.title}
          </h1>
          {post.excerpt && <p className="mt-4 text-lg text-white/75">{post.excerpt}</p>}
          {post.tags && post.tags.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {post.tags.map((t: string) => (
                <Link
                  key={t}
                  to="/blog/tags"
                  hash={slugifyTag(t)}
                  className="inline-flex items-center rounded-full border border-white/20 bg-white/[0.04] px-3 py-1 text-xs font-medium text-white/80 transition-colors hover:border-white hover:text-white"
                >
                  {formatTag(t)}
                </Link>
              ))}
            </div>
          )}
          {post.cover_image_url && (
            <a
              href={post.cover_image_url}
              download={`${slugifyAssetName(post.title)}-Cover.webp`}
              aria-label={`Download cover image for ${post.title}`}
              className="group mt-8 block overflow-hidden rounded-md"
            >
              <img
                src={transformSupabaseImage(post.cover_image_url, {
                  width: 1600,
                  format: "webp",
                  quality: 80,
                })}
                srcSet={coverSrcSet(post.cover_image_url)}
                alt={post.cover_alt || `${post.title} cover image`}
                className="vt-cover block w-full rounded-md transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                width={1600}
                height={1000}
                sizes="(min-width: 1024px) 768px, 100vw"
                fetchPriority="high"
                loading="eager"
                decoding="async"
              />
            </a>
          )}
          <div
            className="prose prose-invert mt-10 max-w-none prose-headings:text-white prose-p:text-white/85 prose-strong:text-white prose-a:text-white prose-li:text-white/85 prose-blockquote:text-white/80"
            dangerouslySetInnerHTML={{ __html: renderedContent }}
          />
          <PostNav prev={prev} next={next} />
          <MentionedIn backlinks={backlinks} />
          <PostEndCta
            postUrl={`https://scottcbusiness.com/blog/${post.slug}`}
            postTitle={post.title}
          />
        </article>
      </div>
      <div
        className="gradient-sweep h-8 w-full shadow-[0_-10px_24px_-12px_oklch(0.6_0.27_305_/_0.45)] sm:h-12 sm:shadow-[0_-16px_32px_-14px_oklch(0.6_0.27_305_/_0.5)] lg:h-16 lg:shadow-[0_-22px_44px_-18px_oklch(0.6_0.27_305_/_0.55)]"
        style={{
          backgroundImage:
            "linear-gradient(90deg, oklch(0.66 0.26 28) 0%, oklch(0.6 0.28 305) 35%, oklch(0.6 0.26 265) 65%, oklch(0.6 0.28 305) 100%)",
        }}
        aria-hidden="true"
      />
    </div>
  );
}

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label="Copy link to this post"
      className="no-print inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/[0.04] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 backdrop-blur transition-colors hover:bg-white/10 hover:text-white"
    >
      {copied ? <Check className="h-3 w-3" /> : <LinkIcon className="h-3 w-3" />}
      <span className="hidden sm:inline">{copied ? "Copied" : "Copy link"}</span>
      <span className="sm:hidden">{copied ? "✓" : "Link"}</span>
    </button>
  );
}

function PostEndCta({ postUrl, postTitle }: { postUrl: string; postTitle: string }) {
  return (
    <section className="no-print mt-16 space-y-6 border-t border-white/10 pt-10">
      {/* About the author */}
      <div className="flex flex-col gap-4 rounded-lg border border-white/15 bg-white/[0.03] p-6 backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:p-8">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
            About the author
          </p>
          <p className="mt-2 text-sm text-white/85">
            Scott Cunningham (@scottcbusiness) is a Toronto-based content strategist for AI, Web3,
            cybersecurity, and SaaS brands, and host of The Tech & Things Show.
          </p>
        </div>
        <Link
          to="/about"
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/30 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-white"
        >
          About Scott Cunningham
        </Link>
      </div>

      {/* Newsletter */}
      <div className="rounded-lg border border-white/15 bg-white/[0.03] p-6 backdrop-blur sm:p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/85">
          The Digital Debrief Newsletter
        </p>
        <h2 className="mt-3 font-display text-2xl tracking-tight text-white sm:text-3xl">
          Subscribe for more field notes
        </h2>
        <p className="mt-3 max-w-xl text-sm text-white/75">
          Practical field notes on AI, Web3, privacy, decentralization, social media, and the
          systems shaping the internet, delivered straight to your inbox.
        </p>
        <NewsletterForm tone="dark" className="mt-5" />
      </div>

      {/* Share + Reach out */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col items-start justify-between gap-4 rounded-lg border border-white/15 bg-white/[0.03] p-6 backdrop-blur">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
              Found it useful?
            </p>
            <p className="mt-2 text-sm text-white/85">
              Share it with someone who'd care, or grab the link.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <ShareMenu url={postUrl} title={postTitle} />
            <CopyLinkButton url={postUrl} />
          </div>
        </div>
        <div className="flex flex-col items-start justify-between gap-4 rounded-lg border border-white/15 bg-white/[0.03] p-6 backdrop-blur">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
              Got a project or question?
            </p>
            <p className="mt-2 text-sm text-white/85">
              I work with brands across AI, Web3, cybersecurity, and SaaS.
            </p>
          </div>
          <Link
            to="/contact"
            className="reach-out-btn inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-foreground transition-transform hover:-translate-y-0.5"
          >
            <Mail className="h-3.5 w-3.5" />
            Reach Out
          </Link>
        </div>
      </div>
    </section>
  );
}

function MentionedIn({ backlinks }: { backlinks: Backlink[] }) {
  if (!backlinks || backlinks.length === 0) return null;
  return (
    <section className="no-print mt-16 border-t border-white/10 pt-8">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
        Mentioned in
      </p>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {backlinks.map((b) => (
          <li key={b.id}>
            <Link
              to="/blog/$slug"
              params={{ slug: b.slug }}
              className="block rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/85 transition-colors hover:border-white/30 hover:text-white"
            >
              {b.title}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
